import { Button } from 'primereact/button';
import { CartItemInterface } from 'graphql/generated/magentoApi';
import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import { ReactElement, useEffect, useState } from 'react';
import { useCartContext } from 'CartContext';
import useViewport from 'hooks/useViewport';
import './CheckoutSummary.scss';

interface CheckoutSummaryProps {
  orderCompleted: boolean;
}

export default function CheckoutSummary(
  props: CheckoutSummaryProps,
): ReactElement {
  const { items, subTotal, total, shipping, taxes } = useCartContext();
  const { width } = useViewport();
  const isMobileWidth = width < 992;
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const { orderCompleted } = props;

  useEffect(() => {
    if (isMobileWidth !== isMobile) {
      setIsMobile(isMobileWidth);
    }
  }, [width, isMobile, isMobileWidth]);

  const cartItem = (item: CartItemInterface) => {
    const subscriptionText =
      item.product.is_subscription === 1
        ? `${item.product.name}\nMonthly Subscription`
        : `${item.product.name}`;
    const subscriptionPricing =
      item.product.is_subscription === 1
        ? `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}/mo`
        : `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}`;
    return (
      <div className="summary__cart__item grid" key={item.uid}>
        <picture className="summary__cart__item__image col-2">
          <img
            alt={String(item.product.name) || ''}
            src={String(item.product.image?.url) || ''}
          />
          <span className="summary__cart__item__quantity">{item.quantity}</span>
        </picture>
        <div
          className="col-6 pl-4"
          style={{ whiteSpace: 'pre-line' }}
        >{`${subscriptionText}`}</div>
        <div className="col-4 text-right">{subscriptionPricing}</div>
      </div>
    );
  };

  const panelTemplate = (options: PanelHeaderTemplateOptions) => {
    const { collapsed, onTogglerClick, className, titleClassName } = options;

    if (isMobile) {
      const toggleIcon = collapsed
        ? 'ml-1 pi pi-chevron-up'
        : 'ml-1 pi pi-chevron-down';
      const header = collapsed ? 'Show Order Summary' : 'Hide Order Summary';
      return (
        <Button
          className="w-full c-button-primary order--sumary--toggle"
          onClick={onTogglerClick}
        >
          <span className="flex align-items-center">
            {header}
            <span className={`ml-1 md:ml-3 ${toggleIcon}`} />
          </span>
          {isMobile && <span>${total}</span>}
        </Button>
      );
    }

    return (
      <div className={className}>
        <span className={titleClassName}>
          {orderCompleted ? 'Order Complete' : 'Order Summary'}
        </span>
      </div>
    );
  };

  return (
    <div className="checkout__summary z-5 col-12 lg:col-4 sticky sm:block">
      <Panel
        className="m-0"
        header="Order Summary"
        headerTemplate={panelTemplate}
        toggleable={isMobile}
        collapsed={isMobile}
      >
        {items.map(item => cartItem(item))}
        {orderCompleted ? (
          <p className="flex justify-content-between text-700 font-medium">
            Thank you for your order
          </p>
        ) : (
          <>
            <hr className="w-full border-none border-bottom-1 border-300 my-3" />
            <div />
            <p className="flex justify-content-between text-500 font-medium my-2">
              Subtotal<span className="text-700">${subTotal}</span>
            </p>
            <p className="flex justify-content-between text-500 font-medium my-2">
              Shipping
              <span className="text-700">
                {Number.isNaN(Number(shipping)) ? shipping : `$${shipping}`}
              </span>
            </p>
            <p className="flex justify-content-between text-500 font-medium my-2">
              Taxes<span className="text-700">${taxes}</span>
            </p>
            <hr className="w-full border-none border-bottom-1 border-300 my-3" />
            <p className="flex justify-content-between text-700 font-medium mt-2 mb-0">
              Total
              <span className="text-900 font-bold">
                <span className="mr-2 text-700 font-medium">USD</span>${total}
              </span>
            </p>
          </>
        )}
      </Panel>
    </div>
  );
}
